import React, { useEffect, useRef, useState } from "react";
import { listAllFloorTables } from "../services/table.service";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { TrimText } from "../../../utils/commonfunction";
import { Canvas, Rect, Circle, Text, Group } from "fabric";
import {
  TABLE_BORDER_COLOR,
  TABLE_FILL_COLOR,
  TABLE_TEXT_COLOR,
} from "../../../utils/Constants";

const DESIGN_WIDTH = 1100;
const DESIGN_HEIGHT = 700;
const STEP = 20;
const MAX_ATTEMPTS = 2000;

export default function SelectTable() {
  const params = useParams();
  const { t } = useTranslation();

  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const fabricCanvasRef = useRef(null);
  const selectedTableRef = useRef(null);

  const [refreshList, setRefreshList] = useState(false);

  const tableDesign = {
    fill: TABLE_FILL_COLOR,
    stroke: TABLE_BORDER_COLOR,
    strokeWidth: 1,
    strokeDashArray: [6, 6],
  };
  const tableTextDesign = {
    fill: TABLE_TEXT_COLOR
  };
  const tableOccupiedDesign = {
    fill: "#BFC3C6",
    stroke: "#000",
    opacity: 0.3
  };

  const selectedTableDesign = {
    fill: "#008a2038",
    stroke: "#008A20",
  };
  const selectedTableTextDesign = {
    fill: "#008A20"
  };
  const tableOccupiedTextDesign = {
    fill: "#BFC3C6"
  };

  /** Scale canvas */
  const scaleCanvasToFit = (fabricCanvas) => {
    if (!fabricCanvas || !containerRef.current) return;

    const scale = containerRef.current.clientWidth / DESIGN_WIDTH;
    fabricCanvas.setZoom(scale);
    fabricCanvas.setWidth(DESIGN_WIDTH * scale);
    fabricCanvas.setHeight(DESIGN_HEIGHT * scale);
    fabricCanvas.renderAll();
  };

  /** Check if group is overlapping */
  const isOverlapping = (target) => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return false;

    const tRect = target.getBoundingRect(true);

    return canvas.getObjects().some((obj) => {
      if (obj === target) return false;
      const oRect = obj.getBoundingRect(true);

      return !(
        tRect.left + tRect.width <= oRect.left ||
        tRect.left >= oRect.left + oRect.width ||
        tRect.top + tRect.height <= oRect.top ||
        tRect.top >= oRect.top + oRect.height
      );
    });
  };

  /** Place group without overlap */
  const placeWithoutOverlap = (group) => {
    let attempts = 0;
    group.setCoords();

    while (isOverlapping(group) && attempts < MAX_ATTEMPTS) {
      group.left += STEP;

      if (group.left + group.getScaledWidth() > DESIGN_WIDTH) {
        group.left = 0;
        group.top += STEP;
      }

      group.setCoords();
      attempts++;
    }

    if (group.left < 0) group.left = 0;
    if (group.top < 0) group.top = 0;
    group.setCoords();
  };

  /** Init canvas */
  useEffect(() => {
    if (!canvasRef.current) return;

    const canvas = new Canvas(canvasRef.current, {
      width: DESIGN_WIDTH,
      height: DESIGN_HEIGHT,
      backgroundColor: "#fff",
      selection: false,
    });

    fabricCanvasRef.current = canvas;

    canvas.on("object:added", (e) => {
      e.target.set({
        lockMovementX: true,
        lockMovementY: true,
        lockScalingX: true,
        lockScalingY: true,
        lockRotation: true,
        hasControls: false,
        hasBorders: false,
      });
    });

    canvas.on("mouse:down", (opt) => {
      const target = opt.target;
      if (!target || !target.tableData) return;
      handleTableSelect(target);
    });

    scaleCanvasToFit(canvas);

    const resize = () => scaleCanvasToFit(canvas);
    window.addEventListener("resize", resize);
    setRefreshList(true);

    return () => {
      window.removeEventListener("resize", resize);
      canvas.dispose();
    };
  }, []);

  /** Handle table select */
  const handleTableSelect = (group) => {
    if (selectedTableRef.current) {
      resetTableStyle(selectedTableRef.current);
    }

    applySelectedStyle(group);
    selectedTableRef.current = group;
    const appData = { action: "table-select", data: group.tableData };
    window.ReactNativeWebView?.postMessage(JSON.stringify(appData));
  };

  const applySelectedStyle = (group) => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;

    group._objects.forEach((obj) => {
      if (obj.type === "rect" || obj.type === "circle") {
        obj.set(selectedTableDesign);
      }
      if (obj.type === "text") {
        obj.set(selectedTableTextDesign);
      }
    });

    group.dirty = true;
    canvas.renderAll();
  };

  const resetTableStyle = (group) => {
    const canvas = fabricCanvasRef.current;
    if (!canvas) return;

    group._objects.forEach((obj) => {
      if (obj.type === "rect") {
        obj.set(tableDesign);
      }

      if (obj.type === "circle") {
        obj.set(tableDesign);
      }

      if (obj.type === "text") {
        obj.set(tableTextDesign);
      }
    });

    group.dirty = true;
    canvas.renderAll();
  };

  /** Add rectangle */
  const addRectangle = (table) => {
    const rect = new Rect({
      width: table.width,
      height: table.height,
      rx: 8,
      ry: 8,
      strokeDashArray: [6, 6],
      strokeWidth: 1,
      originX: "left",
      originY: "top",
      ...(table.is_available ? tableDesign : tableOccupiedDesign),
    });

    const text = new Text(
      TrimText(
        table.table_number,
        Math.floor(table.width / (table.table_text_font_size * 0.6))
      ),
      {
        left: table.width / 2,
        top: table.height / 2,
        originX: "center",
        originY: "center",
        fontSize: table.table_text_font_size,
        fontWeight: "bold",
        ...(table.is_available ? tableTextDesign : tableOccupiedTextDesign),
      }
    );

    const group = new Group([rect, text], {
      left: table.left,
      top: table.top,
      originX: "left",
      originY: "top",
      selectable: table.is_available,
      evented: table.is_available,
      tableData: table,
      hoverCursor: table.is_available ? "pointer" : "not-allowed",
    });

    fabricCanvasRef.current.add(group);
    placeWithoutOverlap(group);
  };

  /** ADD CIRCLE */
  const addCircle = (table) => {
    const circle = new Circle({
      radius: table.radius,
      strokeDashArray: [6, 6],
      originX: "left",
      originY: "top",
      ...(table.is_available ? tableDesign : tableOccupiedDesign),
    });

    const text = new Text(
      TrimText(
        table.table_number,
        Math.floor((table.radius * 2) / (table.table_text_font_size * 0.6))
      ),
      {
        left: table.radius,
        top: table.radius,
        originX: "center",
        originY: "center",
        fontSize: table.table_text_font_size,
        fontWeight: "bold",
        ...(table.is_available ? tableTextDesign : tableOccupiedTextDesign),
      }
    );

    const group = new Group([circle, text], {
      left: table.left,
      top: table.top,
      originX: "left",
      originY: "top",
      selectable: table.is_available,
      evented: table.is_available,
      tableData: table,
      hoverCursor: table.is_available ? "pointer" : "not-allowed",
    });

    fabricCanvasRef.current.add(group);
    placeWithoutOverlap(group);
  };

  /** Loading data for all the tables */
  useEffect(() => {
    if (!fabricCanvasRef.current || !refreshList) return;

    listAllFloorTables(params.restaurant_id).then((res) => {
      res.data?.forEach((table) =>
        table.shape === "round" ? addCircle(table) : addRectangle(table)
      );

      fabricCanvasRef.current.renderAll();
      scaleCanvasToFit(fabricCanvasRef.current);
    });
  }, [refreshList]);


  return (
    <main id="main-content">
      <div className="inner-container">
        {/* <div className="d-flex justify-content-center mb-4">
          <h3>{t("Select a Table")}</h3>
        </div> */}
        <div style={{ padding: "20px", minWidth: "100%", maxWidth: "100%" }}>
          <div ref={containerRef} style={{ width: "100%" }}>
            <canvas ref={canvasRef} />
          </div>
        </div>
      </div>
    </main>
  );
}
